import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, Clock, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

const SNOOZE_OPTIONS = [
    { key: '15min', label: '15 min' },
    { key: '30min', label: '30 min' },
    { key: '1hour', label: '1 hour' },
    { key: 'tomorrow', label: 'Tomorrow' },
    { key: 'next_week', label: 'Next week' },
];

export function ReminderAlert({ reminder, onDismiss, onSnooze }) {
    const navigate = useNavigate();
    const [showSnooze, setShowSnooze] = useState(false);

    return (
        <div className="fixed top-0 left-0 right-0 z-[100] p-3 animate-in slide-in-from-top"
             data-testid="reminder-alert">
            <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-2xl border overflow-hidden"
                 style={{ borderColor: 'rgba(16,185,129,0.3)', boxShadow: '0 8px 40px rgba(16,185,129,0.15)' }}>
                {/* Main alert */}
                <div className="flex items-start gap-3 p-4">
                    <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0 shadow-md">
                        <img src="/icon-192.png" alt="BrainSync" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wider mb-0.5" style={{ color: '#10b981' }}>
                            Reminder
                        </p>
                        <p className="text-sm font-medium text-foreground line-clamp-2"
                           data-testid="reminder-alert-title">
                            {reminder.bookmark_title || 'Time to revisit your bookmark'}
                        </p>
                    </div>
                    <button onClick={() => onDismiss(reminder.reminder_id)}
                            className="text-muted-foreground hover:text-foreground p-1 shrink-0"
                            data-testid="reminder-dismiss">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Actions */}
                <div className="flex border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                    <button
                        onClick={() => {
                            onDismiss(reminder.reminder_id);
                            navigate(`/bookmark/${reminder.bookmark_id}`);
                        }}
                        className="flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium transition-colors hover:bg-secondary/50"
                        style={{ color: '#10b981' }}
                        data-testid="reminder-open"
                    >
                        <Bell className="w-3.5 h-3.5" />
                        Open
                    </button>
                    <div className="w-px" style={{ background: 'rgba(0,0,0,0.06)' }} />
                    <button
                        onClick={() => setShowSnooze(!showSnooze)}
                        className="flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary/50"
                        data-testid="reminder-snooze-toggle"
                    >
                        <Clock className="w-3.5 h-3.5" />
                        Snooze
                        <ChevronDown className={`w-3 h-3 transition-transform ${showSnooze ? 'rotate-180' : ''}`} />
                    </button>
                </div>

                {/* Snooze options */}
                {showSnooze && (
                    <div className="flex flex-wrap gap-1.5 p-3 bg-secondary/30 border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                        {SNOOZE_OPTIONS.map(opt => (
                            <Button
                                key={opt.key}
                                variant="outline"
                                size="sm"
                                className="rounded-full text-xs h-7 px-3"
                                onClick={() => onSnooze(reminder.reminder_id, opt.key)}
                                data-testid={`snooze-${opt.key}`}
                            >
                                {opt.label}
                            </Button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export function ReminderAlerts({ dueReminders, onDismiss, onSnooze }) {
    if (!dueReminders || dueReminders.length === 0) return null;
    // Show only the most recent due reminder
    const latest = dueReminders[dueReminders.length - 1];
    return <ReminderAlert reminder={latest} onDismiss={onDismiss} onSnooze={onSnooze} />;
}
